// Module 20 contracts (AI add-ons stream, ADR-024): AI settings and kill switch, insight and
// decision-support runs, human reviews and the activity log. Messages are i18n keys (PD-015).
// No request here carries, or can carry, an approval, credential, gate or presence command.
import { z } from 'zod';
import { ReportFilterInput } from './safety.ts';

const Uuid = z.uuid();
const trimmed = (max: number) => z.string().trim().min(1, 'v.required').max(max, 'v.tooLong');

export const AI_PROVIDERS = ['none', 'local-rules', 'anthropic'] as const;
export const AI_DOMAINS = ['reporting', 'approvals'] as const;

export const UpdateAiSettingsRequest = z
  .object({
    expectedVersion: z.number().int().min(0),
    enabled: z.boolean(),
    provider: z.enum(AI_PROVIDERS),
    domains: z.array(z.enum(AI_DOMAINS)).max(2),
    includePersonalData: z.boolean(),
    smallGroupThreshold: z.number().int(),
    minimumEvidence: z.number().int(),
    maxEvidenceAgeMinutes: z.number().int(),
    residencyNote: z.string().trim().max(1000, 'v.tooLong').nullable(),
  })
  .strict();
export type UpdateAiSettingsRequest = z.infer<typeof UpdateAiSettingsRequest>;

export const DisableAiRequest = z.object({ reason: trimmed(500) }).strict();

/** PD-605: write-only provider key. Sealed on receipt; never echoed, logged or audited. */
export const SetAiCredentialRequest = z
  .object({
    provider: z.literal('anthropic'),
    apiKey: z.string().trim().min(8, 'v.secretShort').max(4000, 'v.tooLong').regex(/^[\x21-\x7e]+$/, 'v.invalid'),
  })
  .strict();
export type SetAiCredentialRequest = z.infer<typeof SetAiCredentialRequest>;

/** PD-606: a synthetic, data-free request to the provider. */
export const AiConnectionTestRequest = z.object({ provider: z.enum(AI_PROVIDERS) }).strict();

/** PB-20-01: an insight over the report result the caller is looking at (same filter digest). */
export const RequestInsightRequest = z.object({ filter: ReportFilterInput, displayedDigest: z.string().min(16).max(64) }).strict();
/** PB-20-02: a factual summary for one approval task the caller may decide. */
export const RequestDecisionSupportRequest = z.object({ taskId: Uuid }).strict();

export const AI_DISPOSITION_VALUES = ['useful', 'wrong', 'needs_more_evidence', 'accepted_for_follow_up', 'dismissed'] as const;
export const RecordAiReviewRequest = z
  .object({
    disposition: z.enum(AI_DISPOSITION_VALUES),
    recommendationKind: z.string().regex(/^[a-z_]{1,40}$/, 'v.invalid').nullable().optional(),
    note: z.string().trim().max(1000, 'v.tooLong').nullable().optional(),
  })
  .strict();

export const AiRunListQuery = z.object({ kind: z.enum(['insight', 'decision_support']).optional(), limit: z.coerce.number().int().min(1).max(100).default(25) });

export interface AiProviderDto {
  readonly id: (typeof AI_PROVIDERS)[number];
  readonly external: boolean;
  readonly generator: 'rules' | 'language_model' | null;
  readonly available: boolean;
  readonly reason: string | null;
  readonly model: string | null;
  /** Where the adapter's credential comes from (never the credential). */
  readonly credential: 'not_needed' | 'installation' | 'organisation';
}

/** PD-605: provider key status. The key itself is never returned. */
export interface AiCredentialDto {
  readonly provider: 'anthropic';
  /** Which key a call would use now: the organisation's own, the installation's, or none. */
  readonly source: 'organisation' | 'installation' | 'none';
  /** Organisation key version (0 = never stored). */
  readonly version: number;
  readonly updatedAt: string | null;
}

/** PD-606: the latest connection test (metadata only). */
export interface AiConnectionTestDto {
  readonly at: string;
  readonly provider: (typeof AI_PROVIDERS)[number];
  readonly outcome: string;
  readonly model: string | null;
  readonly latencyMs: number;
  readonly inputTokens: number | null;
  readonly outputTokens: number | null;
}

export interface AiSettingsDto {
  readonly enabled: boolean;
  readonly provider: (typeof AI_PROVIDERS)[number];
  readonly domains: ReadonlyArray<(typeof AI_DOMAINS)[number]>;
  readonly includePersonalData: boolean;
  readonly smallGroupThreshold: number;
  readonly minimumEvidence: number;
  readonly maxEvidenceAgeMinutes: number;
  readonly residencyNote: string | null;
  /** 0 = never saved (defaults). */
  readonly version: number;
  readonly updatedAt: string | null;
  readonly disabled: { readonly at: string; readonly by: string | null; readonly reason: string | null } | null;
}

export interface AiSettingsResponse {
  readonly settings: AiSettingsDto;
  readonly providers: readonly AiProviderDto[];
  readonly entitlements: { readonly insights: boolean; readonly decisionSupport: boolean };
  /** The retention record class governing AI runs and reviews (privacy & retention screen). */
  readonly retentionClass: 'ai_records';
  readonly canManage: boolean;
  readonly canDisable: boolean;
  readonly bounds: Readonly<Record<'smallGroupThreshold' | 'minimumEvidence' | 'maxEvidenceAgeMinutes', readonly [number, number]>>;
  /** Offered external providers' key status (PD-605). */
  readonly credentials: readonly AiCredentialDto[];
  readonly lastTest: AiConnectionTestDto | null;
  /** PD-602 (B-13): the only allowed setting for external providers today. */
  readonly externalPersonalData: 'disallowed';
}

export interface AiStatusResponse {
  readonly enabled: boolean;
  readonly generator: 'rules' | 'language_model' | null;
  /** Whether this person can ask for each kind somewhere (organisation settings and own grants). */
  readonly insights: boolean;
  readonly decisionSupport: boolean;
  readonly settingsAccess: boolean;
  readonly activityAccess: boolean;
}

export type AiStatement = { readonly key: string; readonly params?: Readonly<Record<string, string | number>> } | { readonly text: string };

export interface AiRunDto {
  readonly id: string;
  readonly kind: 'insight' | 'decision_support';
  readonly status: 'queued' | 'completed' | 'no_supported_insight' | 'unavailable' | 'cancelled' | 'withheld';
  readonly statusReason: string | null;
  readonly requestedAt: string;
  readonly completedAt: string | null;
  readonly provenance: {
    readonly provider: string;
    readonly model: string | null;
    readonly generator: 'rules' | 'language_model' | null;
    readonly external: boolean;
    readonly promptTemplate: string;
    readonly promptVersion: string;
    readonly settingsVersion: number;
    readonly evidenceDigest: string;
    readonly evidenceAsOf: string;
    readonly personalData: boolean;
  };
  readonly subject:
    | { readonly kind: 'report'; readonly definition: string; readonly filterDigest: string; readonly filter: unknown; readonly siteIds: readonly string[] }
    | { readonly kind: 'approval_task'; readonly taskId: string; readonly visitId: string; readonly visitVersion: number };
  /** Decision support: the visit or task changed after this summary was made; do not rely on it. */
  readonly stale: boolean;
}

export interface AiEvidenceDto {
  readonly metrics: ReadonlyArray<{ readonly id: string; readonly key: string; readonly value: number | null; readonly basis: string }>;
  readonly rows: ReadonlyArray<{ readonly id: string; readonly label: string | null; readonly suppressed: boolean; readonly values: Readonly<Record<string, number | null>> }>;
  readonly facts: ReadonlyArray<{ readonly id: string; readonly key: string; readonly params: Readonly<Record<string, string | number>> }>;
  readonly freeText: { readonly title: string | null; readonly purpose: string | null } | null;
  readonly suppressedRows: number;
}

export interface AiRunDetailResponse extends AiRunDto {
  readonly evidence: AiEvidenceDto | null;
  readonly output: {
    readonly generator: 'rules' | 'language_model';
    readonly findings: ReadonlyArray<{ readonly statement: AiStatement; readonly citations: readonly string[]; readonly confidence: 'low' | 'medium' | 'high' }>;
    readonly recommendations: ReadonlyArray<{ readonly kind: string; readonly route: 'approvals' | 'visit' | 'summary'; readonly statement: AiStatement; readonly citations: readonly string[] }>;
    readonly limitations: readonly AiStatement[];
  } | null;
  /** How many output items the guard discarded (details are in the activity record, not shown). */
  readonly discarded: number;
  readonly reviews: ReadonlyArray<{ readonly id: string; readonly disposition: string; readonly recommendationKind: string | null; readonly note: string | null; readonly reviewer: string; readonly createdAt: string }>;
  readonly dispositions: readonly string[];
}

export interface AiActivityItem {
  readonly id: string;
  readonly kind: 'insight' | 'decision_support';
  readonly status: AiRunDto['status'];
  readonly statusReason: string | null;
  readonly requestedBy: { readonly id: string; readonly name: string };
  readonly requestedAt: string;
  readonly provider: string;
  readonly model: string | null;
  readonly promptVersion: string;
  readonly external: boolean;
  readonly personalData: boolean;
  readonly subject: string;
  readonly siteCount: number;
  readonly discarded: number;
  readonly reviews: number;
  /** PD-603: metadata of the provider call (null when no provider was called). */
  readonly usage: { readonly inputTokens: number | null; readonly outputTokens: number | null; readonly latencyMs: number; readonly attempts: number | null } | null;
}
